/*
 * @Author: Chao Yang
 * @Date: 2019-12-17 02:08:50
 * @LastEditor: Chao Yang
 * @LastEditTime: 2021-07-13 09:07:57
 */
import { Injectable } from '@angular/core';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as Document from 'src/app/domain/model/document';
import { DocumentProxy } from 'src/app/domain/proxy';
import { InfoService } from '../info';
import { SessionService } from '../session';
import * as IDtoAccountGroup from 'src/app/domain/model/account-group/index';

@Injectable()
export class DocumentService {
  constructor(
    private _documentProxy: DocumentProxy,
    private _sessionService: SessionService
  ) { }

  hasPassword(documentId: number) {
    return this._documentProxy.hasPassword(documentId);
  }

  download(documentId: number): void {
    this._documentProxy.download(documentId);
  }

  downloadPackage(resourceId: string): void {
    this._documentProxy.downloadPackage(resourceId);
  }

  flowPaper(id: number): Observable<string> {
    return this._documentProxy.flowPaper(id).pipe(
      map((response) => {
        const data = response.d;
        if (data.hasError) {
          return undefined;
        }
        return '/Flowpaper/split_document.aspx?doc=' + data.fileGuid + '.pdf';
      })
    );
  }

  getDocumentIcon(extension: string): string {
    extension = extension ? extension.toLowerCase() : extension;
    switch (extension) {
      case '.pdf':
        return 'fa fa-file-pdf-o';
      case '.doc':
      case '.docx':
        return 'fa fa-file-word-o';
      case '.xls':
      case '.xlsx':
        return 'fa fa-file-excel-o';
      case '.ppt':
      case '.pptx':
        return 'fa fa-file-powerpoint-o';
      case 'youtube':
        return 'fa fa-youtube-play';
      case 'vimeo':
        return 'fa fa-vimeo';
      default:
        return 'fa fa-file-o';
    }
  }

  getDocumentsWithEffectiveDate(
    fundIds: number[],
    accountIds: number[],
    folderId: number,
    effectiveDateFrom: Date,
    effectiveDateTo: Date,
    accounts: IDtoAccountGroup.IAccountItem[]
  ): Observable<Document.IDocument[]> {
    return this._documentProxy
      .getDocumentsWithEffectiveDate(
        fundIds,
        accountIds,
        folderId,
        effectiveDateFrom,
        effectiveDateTo,
        accounts
      )
      .pipe(
        map((documents) => {
          return documents.sort((d1, d2) => {
            if (d1.posted > d2.posted) {
              return -1;
            } else if (d1.posted < d2.posted) {
              return 1;
            } else {
              return d1.name.localeCompare(d2.name);
            }
          });
        })
      );
  }

  getDocumentsWithPostedDate(
    fundIds: number[],
    accountIds: number[],
    folderId: number,
    postedDateFrom: Date,
    postedDateTo: Date,
    accounts: IDtoAccountGroup.IAccountItem[]
  ): Observable<Document.IDocument[]> {
    return this._documentProxy
      .getDocumentsWithPostedDate(
        fundIds,
        accountIds,
        folderId,
        postedDateFrom,
        postedDateTo,
        new Date().getTimezoneOffset(),
        accounts
      )
      .pipe(
        map((documents) => {
          return documents.sort((d1, d2) => {
            if (d1.posted > d2.posted) {
              return -1;
            } else if (d1.posted < d2.posted) {
              return 1;
            } else {
              return d1.name.localeCompare(d2.name);
            }
          });
        })
      );
  }

  getTaxDocuments(
    fundIds: number[],
    accountIds: number[],
    effectiveDateFrom: Date,
    effectiveDateTo: Date,
    accounts: IDtoAccountGroup.IAccountItem[]
  ): Observable<Document.IDocument[]> {
    return this._documentProxy
      .getTaxDocuments(
        fundIds,
        accountIds,
        effectiveDateFrom,
        effectiveDateTo,
        accounts
      )
      .pipe(
        map((documents) => {
          return documents.sort((d1, d2) => {
            if (d1.posted > d2.posted) {
              return -1;
            } else if (d1.posted < d2.posted) {
              return 1;
            } else {
              return d1.name.localeCompare(d2.name);
            }
          });
        })
      );
  }

  invalid(documentIds: number[]): Observable<number[]> {
    return this._documentProxy.invalid(documentIds);
  }

  link(documentId): string {
    return this._documentProxy.link(documentId, true);
  }

  markRead(documentIds: number[]): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._documentProxy.getShadowUserDefaultObservable();
    }
    return this._documentProxy.markRead(documentIds);
  }

  markUnread(documentIds: number[]): Observable<void> {
    if (this._sessionService.isShadow()) {
      return this._documentProxy.getShadowUserDefaultObservable();
    }
    return this._documentProxy.markUnread(documentIds);
  }

  package(documentIds: number[]): Observable<string> {
    return this._documentProxy.package(documentIds);
  }

  pdfJs(documentId: number): Observable<string> {
    return this._documentProxy.valid(documentId).pipe(
      map((response) => {
        if (response === true) {
          return encodeURIComponent(this._documentProxy.link(documentId, true));
        } else {
          return undefined;
        }
      })
    );
  }

  valid(documentId: number): Observable<boolean> {
    return this._documentProxy.valid(documentId);
  }

  getDocument(documentId: number) {
    return this._documentProxy.getDocument(documentId);
  }

  createVideoAudit(documentId: number) {
    return this._documentProxy.createVideoAudit(documentId, true);
  }

  newGetDocuments(
    dealrelationshipId: number,
    start: Date,
    end: Date,
    byEffectiveDate: boolean
  ) {
    return this._documentProxy.newGetDocuments(
      dealrelationshipId,
      start,
      end,
      byEffectiveDate
    );
  }

  newGetAllDocuments(
    fundIds: number[],
    companyIds: number[],
    start: Date,
    end: Date,
    byEffectiveDate: boolean
  ) {
    return this._documentProxy.newGetAllDocuments(
      fundIds,
      companyIds,
      start,
      end,
      byEffectiveDate
    );
  }

  newGetTaxocuments(fundIds: number[], companyIds: number[]) {
    return this._documentProxy.newGetTaxocuments(fundIds, companyIds);
  }
}
